/**
 * WizardStep3Test — Krok 3: Test mTLS / Diagnostyka połączenia.
 *
 * Walidacja przejścia do kroku 4: udany Test mTLS (success=true) lub udany Health Check.
 *
 * Auto-run Health Check przy wejściu na krok (jeśli jeszcze nie był uruchamiany w sesji).
 *
 * Props:
 *   - onCompleted (callback)  — wywoływane gdy test przejdzie pomyślnie
 */
import { useState, useEffect } from "react";
import { logger } from "../../../utils/logger";
import { toast } from "sonner";
import { Shield, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { runHealthCheck, runMtlsTest, runMtlsDiagnose } from "../services/ksefApi";
import { safeDetailMessage, safeErrorMessage } from "../utils/errorMessages";

export const WizardStep3Test = ({ onCompleted }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // {success, message, source, details}
  const [autoRunAttempted, setAutoRunAttempted] = useState(false);

  const runFullTest = async () => {
    setRunning(true);
    setResult(null);

    try {
      // 1) Najpierw mTLS Test (bo to najszybsze potwierdzenie połączenia)
      const mtlsRes = await runMtlsTest();
      if (mtlsRes.data?.success) {
        const passed = {
          success: true,
          source: "mTLS Test",
          message: "Połączenie mTLS z KSeF zakończone sukcesem",
          details: {
            http_status: mtlsRes.data.http_status,
            duration_ms: mtlsRes.data.duration_ms,
            ksef_env: mtlsRes.data.ksef_env,
          },
        };
        setResult(passed);
        toast.success("Test połączenia zakończony sukcesem");
        if (onCompleted) onCompleted();
        return;
      }

      // 2) Jeśli mTLS Test padł — zrób Diagnostykę z dokładnym error_type
      const diagRes = await runMtlsDiagnose();
      const failed = {
        success: false,
        source: "Diagnostyka mTLS",
        message:
          safeErrorMessage(diagRes.data) ||
          String(diagRes.data?.error_message || "Połączenie z KSeF nie powiodło się"),
        details: {
          mtls: diagRes.data?.mtls,
          error_type: diagRes.data?.error_type,
          cert_loaded: diagRes.data?.cert_loaded,
          key_loaded: diagRes.data?.key_loaded,
        },
      };
      setResult(failed);
      toast.error(`Test nieudany: ${String(diagRes.data?.error_type || "BŁĄD")}`);
    } catch (err) {
      logger.error("Test error:", err);
      const errorMsg =
        safeDetailMessage(err.response) ||
        safeErrorMessage(err) ||
        "Błąd komunikacji z backendem";
      setResult({
        success: false,
        source: "Błąd",
        message: errorMsg,
        details: {},
      });
      toast.error("Błąd testu połączenia");
    } finally {
      setRunning(false);
    }
  };

  // Auto-run health check przy pierwszym wejściu na ten krok
  useEffect(() => {
    if (!autoRunAttempted) {
      setAutoRunAttempted(true);
      // Najpierw szybki health check (cache na backendzie), jeśli OK → completed
      runHealthCheck()
        .then((res) => {
          if (res.data?.status === "ok") {
            setResult({
              success: true,
              source: "Health Check",
              message: "Konfiguracja KSeF jest poprawna (Health Check OK)",
              details: { detail: res.data?.detail },
            });
            if (onCompleted) onCompleted();
          }
        })
        .catch(() => {
          // Cicho — user kliknie "Sprawdź" ręcznie
        });
    }
  }, [autoRunAttempted, onCompleted]);

  return (
    <div className="space-y-6" data-testid="wizard-step3">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-[#0F1F3D]">Krok 3: Weryfikacja połączenia</h2>
        </div>
        <p className="text-sm text-gray-600">
          Testujemy mTLS z Twoim certyfikatem łącząc się z produkcyjnym API KSeF.
          To weryfikuje, czy certyfikat, klucz i hasło są poprawne.
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        {running && (
          <div className="flex items-center gap-3 text-blue-700">
            <Loader2 className="w-6 h-6 animate-spin" />
            <div>
              <p className="font-medium">Testowanie mTLS...</p>
              <p className="text-xs text-blue-600">Łączenie z ksef.mf.gov.pl</p>
            </div>
          </div>
        )}

        {!running && !result && (
          <div className="text-center py-4">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-500">Automatyczne sprawdzenie połączenia...</p>
          </div>
        )}

        {!running && result && (
          <div>
            <div className="flex items-start gap-3 mb-3">
              {result.success ? (
                <CheckCircle className="w-7 h-7 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="w-7 h-7 text-red-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p
                  className={`font-medium text-base ${
                    result.success ? "text-green-800" : "text-red-800"
                  }`}
                >
                  {result.success ? "Połączenie OK" : "Połączenie nieudane"}
                </p>
                <p
                  className={`text-sm mt-1 ${
                    result.success ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {result.message}
                </p>
                <p className="text-xs text-gray-500 mt-2">Źródło: {result.source}</p>
              </div>
            </div>

            {result.success && result.details?.http_status && (
              <div className="text-xs text-gray-600 space-y-1 ml-10 mb-3">
                <p>✓ Środowisko: <strong>{result.details.ksef_env?.toUpperCase() || "PROD"}</strong></p>
                <p>✓ HTTP status: <strong>{result.details.http_status}</strong></p>
                {result.details.duration_ms && (
                  <p>✓ Czas: {result.details.duration_ms} ms</p>
                )}
              </div>
            )}

            {!result.success && (
              <div className="text-xs text-red-700 space-y-1 ml-10 mb-3 bg-red-50 p-3 rounded border border-red-200">
                {result.details?.mtls && <p>• mTLS: <strong>{result.details.mtls}</strong></p>}
                {result.details?.error_type && (
                  <p>• Typ błędu: <strong>{result.details.error_type}</strong></p>
                )}
                {result.details?.cert_loaded !== undefined && (
                  <p>• Certyfikat: {result.details.cert_loaded ? "✓ załadowany" : "✗ NIE załadowany"}</p>
                )}
                {result.details?.key_loaded !== undefined && (
                  <p>• Klucz: {result.details.key_loaded ? "✓ załadowany" : "✗ NIE załadowany"}</p>
                )}
              </div>
            )}

            <Button
              onClick={runFullTest}
              variant="outline"
              size="sm"
              className="mt-2"
              data-testid="wizard-retest-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              {result.success ? "Sprawdź ponownie" : "Spróbuj ponownie"}
            </Button>
          </div>
        )}
      </div>

      {!result && !running && (
        <Button
          onClick={runFullTest}
          className="w-full"
          data-testid="wizard-runtest-btn"
        >
          <Shield className="w-4 h-4 mr-2" />
          Sprawdź połączenie z KSeF
        </Button>
      )}
    </div>
  );
};

export default WizardStep3Test;
